import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button, Carousel, Tabs } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import { ChatIcon, CameraIcon, XIcon, ArrowCircleLeftIcon, ArrowCircleRightIcon } from '../../icons';
import { CommentList } from '../../Comment';
import { Comment, CommentProps } from '../../Comment/Comment';
import { PhotoGrid } from '../../PhotoGallery/PhotoGrid';
import { PhotoOverlayInfo, PhotoThumbnailInfo, PhotoMetadata } from '../../PhotoGallery/PhotoOverlayInfo';
import { handleGalleryImageLoad } from '../../PhotoGallery/galleryImageLoad';
import SpecDrawer from '../../SpecDrawer/SpecDrawer';
import ProfileSection from './ProfileSection';
import '../Profile.css';

export interface UserPostsSectionProps {
  userName: string;
  comments: Omit<CommentProps, 'className'>[];
  photos: string[];
  photoMetadata?: PhotoMetadata[];
}

export const UserPostsSection: React.FC<UserPostsSectionProps> = ({
  userName,
  comments,
  photos,
  photoMetadata,
}) => {
  const [activeTab, setActiveTab] = useState('comments');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('comments');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<CarouselRef>(null);
  const isAnimatingRef = useRef(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commentsWithEventInfo = useMemo(
    () => comments.map(c => ({ ...c, showEventInfo: true })),
    [comments],
  );

  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeGallery = () => setIsModalOpen(false);

  const goToPrev = () => {
    if (isAnimatingRef.current || currentIndex <= 0) return;
    isAnimatingRef.current = true;
    carouselRef.current?.goTo(currentIndex - 1);
  };

  const goToNext = () => {
    if (isAnimatingRef.current || currentIndex >= photos.length - 1) return;
    isAnimatingRef.current = true;
    carouselRef.current?.goTo(currentIndex + 1);
  };

  const handleBeforeChange = (current: number, next: number) => {
    if (current !== next) isAnimatingRef.current = true;
  };

  const handleAfterChange = (current: number) => {
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    animationTimeoutRef.current = setTimeout(() => { isAnimatingRef.current = false; }, 100);
    setCurrentIndex(current);
  };

  useEffect(() => {
    if (!isModalOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToNext(); }
      else if (e.key === 'Escape') closeGallery();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = originalOverflow; };
  }, [isModalOpen, currentIndex, photos.length]);

  const renderCommentList = (items: Omit<CommentProps, 'className'>[]) => (
    <div className="view-all-drawer__comment-list">
      {items.length === 0 ? (
        <div className="view-all-drawer__empty">No comments yet</div>
      ) : (
        items.map((comment, i) => <Comment key={i} {...comment} />)
      )}
    </div>
  );

  const renderPhotoGrid = (photoList: string[]) => {
    if (photoList.length === 0) {
      return <div className="view-all-drawer__empty">No photos yet</div>;
    }
    return (
      <div className="photo-gallery-grid">
        {photoList.map((src, index) => (
          <div key={index} className="photo-gallery-grid-item">
            <img
              src={src}
              style={{ objectFit: 'cover', width: '100%', height: '100%', cursor: 'pointer' }}
              onClick={() => openGallery(index)}
              alt={`Photo ${index + 1}`}
            />
            {photoMetadata && <PhotoThumbnailInfo metadata={photoMetadata[index]} />}
          </div>
        ))}
      </div>
    );
  };

  const tabItems = useMemo(() => [
    {
      key: 'comments',
      label: (
        <span className="profile-posts__tab-label">
          <ChatIcon size="s" tone="inherit" />
          Comments
        </span>
      ),
      children: (
        <div className="profile-posts__tab-content">
          <CommentList
            comments={commentsWithEventInfo}
            maxComments={3}
          />
        </div>
      ),
    },
    {
      key: 'photos',
      label: (
        <span className="profile-posts__tab-label">
          <CameraIcon size="s" tone="inherit" />
          Photos
        </span>
      ),
      children: (
        <div className="profile-posts__tab-content">
          <PhotoGrid
            photos={photos}
            maxPhotos={9}
            photoMetadata={photoMetadata}
            showOverlayUserInfo
          />
        </div>
      ),
    },
  ], [commentsWithEventInfo, photos, photoMetadata]);

  const customTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      <div
        style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          margin: '0 auto 24px',
          flexShrink: 0,
        }}
      />
      {userName}&apos;s Posts
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '8px',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <button onClick={() => setDrawerOpen(false)} aria-label="Close drawer" className="drawer-close-button">
          <XIcon size="m" tone="white" />
        </button>
      </div>
    </div>
  );

  return (
    <ProfileSection
      title={`${userName}'s Posts`}
      rightAction={<Button size="small" onClick={() => setDrawerOpen(true)}>View All</Button>}
    >
      <div className="profile-posts__tabs">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          items={tabItems}
        />
      </div>

      <SpecDrawer
        title={customTitle}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        size="large"
        swipeable={false}
        closable={false}
        zIndex={1100}
        keyboard={!isModalOpen}
      >
        <div className="view-all-drawer__content">
          <Tabs
            activeKey={drawerTab}
            onChange={setDrawerTab}
            centered
            items={[
              {
                key: 'comments',
                label: (
                  <span className="profile-posts__tab-label">
                    <ChatIcon size="s" tone="inherit" />
                    Comments
                  </span>
                ),
                children: renderCommentList(commentsWithEventInfo),
              },
              {
                key: 'photos',
                label: (
                  <span className="profile-posts__tab-label">
                    <CameraIcon size="s" tone="inherit" />
                    Photos
                  </span>
                ),
                children: renderPhotoGrid(photos),
              },
            ]}
          />
        </div>
      </SpecDrawer>

      {isModalOpen && createPortal(
        <div className="photo-gallery-overlay">
          <div className="photo-gallery-backdrop" onClick={closeGallery} aria-hidden="true" />
          <button className="photo-gallery-close" onClick={closeGallery} aria-label="Close gallery">
            <XIcon size="m" tone="white" />
          </button>
          <button
            className="photo-gallery-arrow photo-gallery-arrow-prev"
            onClick={goToPrev}
            disabled={currentIndex === 0}
            style={{ opacity: currentIndex === 0 ? 0.3 : 1 }}
            aria-label="Previous photo"
          >
            <ArrowCircleLeftIcon size="xl" tone="white" />
          </button>
          <button
            className="photo-gallery-arrow photo-gallery-arrow-next"
            onClick={goToNext}
            disabled={currentIndex === photos.length - 1}
            style={{ opacity: currentIndex === photos.length - 1 ? 0.3 : 1 }}
            aria-label="Next photo"
          >
            <ArrowCircleRightIcon size="xl" tone="white" />
          </button>
          <div className="photo-gallery-carousel-wrapper">
            <Carousel
              ref={carouselRef}
              initialSlide={currentIndex}
              dots={false}
              swipeToSlide={false}
              draggable={false}
              beforeChange={handleBeforeChange}
              afterChange={handleAfterChange}
              speed={300}
              waitForAnimate
            >
              {photos.map((src, idx) => (
                <div key={idx} className="photo-gallery-slide" onClick={closeGallery}>
                  <div className="photo-gallery-image-wrapper">
                    <img
                      src={src}
                      alt={`Photo ${idx + 1}`}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      onLoad={handleGalleryImageLoad}
                      style={{ userSelect: 'none', WebkitUserDrag: 'none' } as React.CSSProperties}
                    />
                    {photoMetadata && (
                      <PhotoOverlayInfo showUserInfo metadata={photoMetadata[idx]} />
                    )}
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
          <div className="photo-gallery-dots">
            {photos.map((_, idx) => (
              <span key={idx} className={`photo-gallery-dot ${idx === currentIndex ? 'active' : ''}`} />
            ))}
          </div>
        </div>,
        document.body
      )}
    </ProfileSection>
  );
};

export default UserPostsSection;
